// DeepSeek Chat System Data Structure
// Systematic organization for Supabase migration

export const chatTypes = {
  quickQuiz: {
    id: 'quick-quiz',
    name: 'Quick Quiz',
    icon: '🎯',
    description: 'Generate instant quiz questions about the current topic',
    color: 'bg-blue-500',
    prompt: 'Generate 5 multiple-choice questions about {topic} for HKDSE English Language students. Include explanations for each answer.',
    returnAction: 'quizlet'
  },
  drillExercise: {
    id: 'drill-exercise',
    name: 'Follow Me',
    icon: '📚',
    description: 'Practice exercises with step-by-step guidance',
    color: 'bg-green-500',
    prompt: 'Create a comprehensive drill exercise for {topic} with step-by-step practice questions, examples, and progressive difficulty for HKDSE students.',
    returnAction: 'drill'
  },
  directChat: {
    id: 'direct-chat',
    name: 'Chat with AI',
    icon: '💬',
    description: 'Direct conversation with AI tutor',
    color: 'bg-purple-500',
    prompt: 'You are an expert HKDSE English Language tutor. Help the student with questions about {topic}. Provide clear explanations and examples.',
    returnAction: 'chat'
  }
};

export const chatContexts = {
  // Parts of Speech Contexts
  'parts-of-speech': {
    id: 'parts-of-speech',
    title: '詞性分析 (Parts of Speech)',
    description: 'Word classification and analysis',
    topics: {
      'nouns': {
        id: 'nouns',
        title: '名詞 (Nouns)',
        subtopics: {
          'proper': {
            id: 'proper-nouns',
            title: '專有名詞 (Proper Nouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Proper Nouns',
            keywords: ['proper nouns', 'capitalization', 'specific names', 'places', 'people']
          },
          'common': {
            id: 'common-nouns',
            title: '普通名詞 (Common Nouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Common Nouns',
            keywords: ['common nouns', 'general names', 'countable', 'uncountable', 'articles']
          },
          'abstract': {
            id: 'abstract-nouns',
            title: '抽象名詞 (Abstract Nouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Abstract Nouns',
            keywords: ['abstract nouns', 'ideas', 'qualities', 'emotions', 'concepts']
          },
          'concrete': {
            id: 'concrete-nouns',
            title: '具體名詞 (Concrete Nouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Concrete Nouns',
            keywords: ['concrete nouns', 'tangible', 'physical objects', 'senses']
          },
          'collective': {
            id: 'collective-nouns',
            title: '集合名詞 (Collective Nouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Collective Nouns',
            keywords: ['collective nouns', 'groups', 'teams', 'subject-verb agreement']
          }
        }
      },
      'pronouns': {
        id: 'pronouns',
        title: '代詞 (Pronouns)',
        subtopics: {
          'personal': {
            id: 'personal-pronouns',
            title: '人稱代詞 (Personal Pronouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Personal Pronouns',
            keywords: ['personal pronouns', 'subject', 'object', 'I', 'me', 'they', 'them']
          },
          'possessive': {
            id: 'possessive-pronouns',
            title: '所有格代詞 (Possessive Pronouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Possessive Pronouns',
            keywords: ['possessive pronouns', 'ownership', 'my', 'mine', 'your', 'yours']
          },
          'reflexive': {
            id: 'reflexive-pronouns',
            title: '反身代詞 (Reflexive Pronouns)',
            context: 'HKDSE English Grammar - Parts of Speech - Reflexive Pronouns',
            keywords: ['reflexive pronouns', 'myself', 'yourself', 'themselves', 'emphasis']
          }
        }
      },
      'verbs': {
        id: 'verbs',
        title: '動詞 (Verbs)',
        subtopics: {
          'action': {
            id: 'action-verbs',
            title: '動作動詞 (Action Verbs)',
            context: 'HKDSE English Grammar - Parts of Speech - Action Verbs',
            keywords: ['action verbs', 'physical actions', 'mental actions', 'transitive', 'intransitive']
          },
          'linking': {
            id: 'linking-verbs',
            title: '連繫動詞 (Linking Verbs)',
            context: 'HKDSE English Grammar - Parts of Speech - Linking Verbs',
            keywords: ['linking verbs', 'be', 'seem', 'appear', 'become', 'complement']
          },
          'auxiliary': {
            id: 'auxiliary-verbs',
            title: '助動詞 (Auxiliary Verbs)',
            context: 'HKDSE English Grammar - Parts of Speech - Auxiliary Verbs',
            keywords: ['auxiliary verbs', 'helping verbs', 'be', 'have', 'do', 'modal verbs']
          }
        }
      }
    }
  },
  
  // Verb Tenses Contexts
  'verb-tenses': {
    id: 'verb-tenses',
    title: '動詞時態 (Verb Tenses)',
    description: '12 major tenses with timeline',
    topics: {
      'present': {
        id: 'present-tenses',
        title: 'Present Tenses',
        subtopics: {
          'simple-present': {
            id: 'simple-present',
            title: 'Simple Present',
            context: 'HKDSE English Grammar - Verb Tenses - Simple Present',
            keywords: ['simple present', 'habits', 'routines', 'general truths', 'third person singular']
          },
          'present-continuous': {
            id: 'present-continuous',
            title: 'Present Continuous',
            context: 'HKDSE English Grammar - Verb Tenses - Present Continuous',
            keywords: ['present continuous', 'present progressive', 'ing form', 'now', 'currently']
          },
          'present-perfect': {
            id: 'present-perfect',
            title: 'Present Perfect',
            context: 'HKDSE English Grammar - Verb Tenses - Present Perfect',
            keywords: ['present perfect', 'have', 'has', 'past participle', 'experience', 'just', 'already']
          }
        }
      },
      'past': {
        id: 'past-tenses',
        title: 'Past Tenses',
        subtopics: {
          'simple-past': {
            id: 'simple-past',
            title: 'Simple Past',
            context: 'HKDSE English Grammar - Verb Tenses - Simple Past',
            keywords: ['simple past', 'past form', 'yesterday', 'last week', 'ago', 'regular', 'irregular']
          },
          'past-continuous': {
            id: 'past-continuous',
            title: 'Past Continuous',
            context: 'HKDSE English Grammar - Verb Tenses - Past Continuous',
            keywords: ['past continuous', 'was', 'were', 'ing form', 'interrupted actions']
          },
          'past-perfect': {
            id: 'past-perfect',
            title: 'Past Perfect',
            context: 'HKDSE English Grammar - Verb Tenses - Past Perfect',
            keywords: ['past perfect', 'had', 'past participle', 'before', 'after', 'sequence']
          }
        }
      },
      'future': {
        id: 'future-tenses',
        title: 'Future Tenses',
        subtopics: {
          'simple-future': {
            id: 'simple-future',
            title: 'Simple Future',
            context: 'HKDSE English Grammar - Verb Tenses - Simple Future',
            keywords: ['simple future', 'will', 'going to', 'predictions', 'plans', 'tomorrow']
          },
          'future-continuous': {
            id: 'future-continuous',
            title: 'Future Continuous',
            context: 'HKDSE English Grammar - Verb Tenses - Future Continuous',
            keywords: ['future continuous', 'will be', 'ing form', 'future progress']
          },
          'future-perfect': {
            id: 'future-perfect',
            title: 'Future Perfect',
            context: 'HKDSE English Grammar - Verb Tenses - Future Perfect',
            keywords: ['future perfect', 'will have', 'past participle', 'by', 'completion']
          }
        }
      }
    }
  },

  // Sentence Structure Contexts
  'sentence-structure': {
    id: 'sentence-structure',
    title: '句子結構 (Sentence Structure)',
    description: 'Five basic patterns and complex structures',
    topics: {
      'basic-patterns': {
        id: 'basic-patterns',
        title: 'Basic Sentence Patterns',
        subtopics: {
          'sv': {
            id: 'subject-verb',
            title: 'Subject + Verb',
            context: 'HKDSE English Grammar - Sentence Structure - Subject Verb Pattern',
            keywords: ['subject verb', 'S+V', 'intransitive verbs', 'simple sentences']
          },
          'svo': {
            id: 'subject-verb-object',
            title: 'Subject + Verb + Object',
            context: 'HKDSE English Grammar - Sentence Structure - Subject Verb Object',
            keywords: ['subject verb object', 'S+V+O', 'transitive verbs', 'direct object']
          },
          'svc': {
            id: 'subject-verb-complement',
            title: 'Subject + Verb + Complement',
            context: 'HKDSE English Grammar - Sentence Structure - Subject Verb Complement',
            keywords: ['subject verb complement', 'S+V+C', 'linking verbs', 'subject complement']
          }
        }
      },
      'voice': {
        id: 'voice',
        title: 'Active and Passive Voice',
        subtopics: {
          'active': {
            id: 'active-voice',
            title: 'Active Voice',
            context: 'HKDSE English Grammar - Sentence Structure - Active Voice',
            keywords: ['active voice', 'subject performs action', 'direct', 'clear']
          },
          'passive': {
            id: 'passive-voice',
            title: 'Passive Voice',
            context: 'HKDSE English Grammar - Sentence Structure - Passive Voice',
            keywords: ['passive voice', 'be + past participle', 'subject receives action', 'by agent']
          }
        }
      }
    }
  },

  // Common Errors Contexts
  'common-errors': {
    id: 'common-errors',
    title: '常見錯誤 (Common Errors)',
    description: 'Typical mistakes and corrections',
    topics: {
      'subject-verb-agreement': {
        id: 'subject-verb-agreement',
        title: 'Subject-Verb Agreement',
        subtopics: {
          'singular-plural': {
            id: 'singular-plural-agreement',
            title: 'Singular-Plural Agreement',
            context: 'HKDSE English Grammar - Common Errors - Subject Verb Agreement',
            keywords: ['subject verb agreement', 'singular', 'plural', 'third person', 'collective nouns']
          }
        }
      },
      'pronoun-errors': {
        id: 'pronoun-errors',
        title: 'Pronoun Errors',
        subtopics: {
          'subject-object': {
            id: 'subject-object-pronouns',
            title: 'Subject vs Object Pronouns',
            context: 'HKDSE English Grammar - Common Errors - Pronoun Usage',
            keywords: ['pronoun errors', 'subject pronouns', 'object pronouns', 'I vs me', 'who vs whom']
          }
        }
      }
    }
  }
};

// Mock Chat History Data
export const mockChatHistory = [
  {
    id: 'chat-history-001',
    userId: 'user-001',
    contextId: 'common-nouns',
    chatType: 'quick-quiz',
    timestamp: '2024-01-15T10:30:00Z',
    title: 'Common Nouns Quiz',
    messages: [
      {
        id: 'msg-001',
        type: 'user',
        content: 'Generate quiz about common nouns',
        timestamp: '2024-01-15T10:30:00Z'
      },
      {
        id: 'msg-002',
        type: 'ai',
        content: 'Here are 5 multiple-choice questions about Common Nouns...',
        timestamp: '2024-01-15T10:30:15Z'
      }
    ],
    savedAs: 'quizlet',
    downloaded: true
  },
  {
    id: 'chat-history-002',
    userId: 'user-001',
    contextId: 'simple-present',
    chatType: 'drill-exercise',
    timestamp: '2024-01-15T11:45:00Z',
    title: 'Simple Present Drill',
    messages: [
      {
        id: 'msg-003',
        type: 'user',
        content: 'I need practice with simple present tense',
        timestamp: '2024-01-15T11:45:00Z'
      },
      {
        id: 'msg-004',
        type: 'ai',
        content: 'Let\'s practice Simple Present step by step...',
        timestamp: '2024-01-15T11:45:20Z'
      }
    ],
    savedAs: 'drill',
    downloaded: false
  }
];

// Saved Content Data Structure
export const savedContent = {
  quizlets: [
    {
      id: 'quizlet-001',
      title: 'Common Nouns Quiz',
      contextId: 'common-nouns',
      createdAt: '2024-01-15T10:30:00Z',
      questions: [
        {
          id: 'q-001',
          question: 'Which of the following is a common noun?',
          options: ['London', 'city', 'Mary', 'Christmas'],
          correctAnswer: 1,
          explanation: 'City is a common noun because it refers to a general place, not a specific one.'
        }
      ]
    }
  ],
  drills: [
    {
      id: 'drill-001',
      title: 'Simple Present Practice',
      contextId: 'simple-present',
      createdAt: '2024-01-15T11:45:00Z',
      exercises: [
        {
          id: 'ex-001',
          instruction: 'Complete the sentence with the correct form of the verb',
          sentence: 'She _____ (study) English every day.',
          answer: 'studies',
          explanation: 'Third person singular subjects take -s/-es in simple present.'
        }
      ]
    }
  ]
};

// Chat Configuration
export const chatConfig = {
  apiEndpoint: 'https://api.deepseek.com/chat/completions',
  model: 'deepseek-chat',
  maxTokens: 1000,
  temperature: 0.7,
  systemPrompt: `You are an expert HKDSE English Language tutor. Your role is to help students understand English grammar concepts clearly and effectively. 

Guidelines:
1. Always provide clear, concise explanations
2. Use examples relevant to HKDSE syllabus
3. Break down complex concepts into simple steps
4. Encourage students and provide positive feedback
5. Use both English and Chinese when helpful
6. Focus on practical application for exams
7. Provide memory tips and tricks when appropriate

Remember: You are helping students prepare for the Hong Kong Diploma of Secondary Education (HKDSE) English Language examination.`,
  
  quickQuizPrompt: `Generate {count} multiple-choice questions about {topic} for HKDSE English Language students. 

Format each question as:
Question: [question text]
A) [option A]
B) [option B] 
C) [option C]
D) [option D]
Correct Answer: [letter]
Explanation: [detailed explanation]

Focus on exam-relevant concepts and common mistakes students make.`,

  drillPrompt: `Create a comprehensive drill exercise for {topic} with progressive difficulty for HKDSE students.

Include:
1. Brief concept review
2. 5-7 practice questions with increasing difficulty
3. Step-by-step solutions
4. Common mistakes to avoid
5. Exam tips
6. Additional practice suggestions

Make it interactive and engaging for self-study.`,

  chatPrompt: `You are helping a HKDSE student with {topic}. Provide clear explanations, examples, and answer any questions they have. Focus on exam preparation and practical application.`
};

export default {
  chatTypes,
  chatContexts,
  mockChatHistory,
  savedContent,
  chatConfig
}; 